import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { client, createCacheKey } from '../lib/sanityClient';
import imageUrlBuilder from '@sanity/image-url';

// Initialize the image URL builder
const builder = imageUrlBuilder(client);

interface ArtworkImage {
  _type: string;
  asset: {
    _ref: string;
  };
}

interface Artist {
  _id: string;
  name: string;
  slug: string;
  featuredArtwork?: {
    image: ArtworkImage;
  };
}

const Artists: FC = () => {
  const { data: artists = [], isLoading } = useQuery<Artist[]>({
    queryKey: createCacheKey('artists-list'),
    queryFn: async () => {
      const query = `*[_type == "artist"] | order(name asc) {
        _id,
        name,
        "slug": slug.current,
        "featuredArtwork": *[_type == "artwork" && references(^._id)][0] {
          image
        }
      }`;
      return client.fetch<Artist[]>(query);
    }
  });
  
  // State to track which artist is being hovered
  const [hoveredArtist, setHoveredArtist] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading artists...</div>
      </div>
    );
  }

  if (!artists.length) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="text-xl text-gray-600">No artists found</div>
      </div>
    );
  }

  // Function to get image URL from Sanity image reference
  const getImageUrl = (image: ArtworkImage) => {
    return builder.image(image).width(1920).url();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white overflow-hidden relative">
      {/* Background artwork images */}
      {artists.map((artist) => (
        artist.featuredArtwork?.image && (
          <div 
            key={`bg-${artist._id}`}
            className={`absolute inset-0 transition-opacity duration-500 ${hoveredArtist === artist._id ? 'opacity-100' : 'opacity-0'}`}
            style={{
              backgroundImage: `url(${getImageUrl(artist.featuredArtwork.image)})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              zIndex: 0
            }}
          >
            {/* Overlay to ensure text readability */}
            <div className="absolute inset-0 bg-black/30"></div>
          </div>
        )
      ))}
      
      {/* Artist names list */}
      <div className="w-full max-w-md px-4 -mt-32 relative z-10"> 
        <div className="space-y-6">
          {artists.map((artist) => (
            <div 
              key={artist._id} 
              className="text-center"
              onMouseEnter={() => setHoveredArtist(artist._id)}
              onMouseLeave={() => setHoveredArtist(null)}
            >
              <Link 
                to={`/artist/${artist.slug}`}
                className={`transition-colors duration-300 ${hoveredArtist === artist._id ? 'text-white' : 'text-black hover:text-gray-600'}`}
              >
                <h2 className="text-3xl font-book-antiqua tracking-wide artist-name">
                  {artist.name}
                </h2>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Artists;
